import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

interface PositionIndicatorProps {
  currentPosition: number;
  bestPosition: number;
  likelyPosition: number;
  controlsDestiny: boolean;
  contextualLabel?: string;
  size?: 'small' | 'medium';
}

export const PositionIndicator: React.FC<PositionIndicatorProps> = ({
  currentPosition,
  bestPosition,
  likelyPosition,
  controlsDestiny,
  contextualLabel,
  size = 'small'
}) => {
  // Determine the color and trend based on best possible position
  const getPositionColor = () => {
    if (bestPosition === 1) return 'success';
    if (bestPosition <= 3) return 'info';
    if (bestPosition <= currentPosition) return 'warning';
    return 'default';
  };

  const getTrendIcon = () => {
    if (bestPosition < currentPosition) {
      return <TrendingUp fontSize="inherit" />;
    } else if (bestPosition > currentPosition) {
      return <TrendingDown fontSize="inherit" />;
    } else {
      return <TrendingFlat fontSize="inherit" />;
    }
  };

  const getTooltipContent = () => {
    const destinyText = controlsDestiny 
      ? 'Controls own destiny' 
      : 'Depends on other results';

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Position Analysis
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Current: {currentPosition}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Best possible: {bestPosition} {contextualLabel && `(${contextualLabel})`}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Most likely: {likelyPosition}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
          {destinyText}
        </Typography>
      </Box>
    );
  };

  return (
    <Tooltip title={getTooltipContent()} arrow placement="top">
      <Chip
        icon={getTrendIcon()}
        label={bestPosition}
        color={getPositionColor() as any}
        size={size}
        variant={controlsDestiny ? 'filled' : 'outlined'}
        sx={{
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          height: size === 'small' ? 24 : 32,
          '& .MuiChip-icon': {
            fontSize: size === 'small' ? '0.875rem' : '1rem'
          }
        }}
      />
    </Tooltip>
  );
};
